/**
 * Family Event taxonomy — 200+ event types across 10 top-level categories,
 * covering major religions and cultures. Each event has:
 *   - value    : machine key stored on the event record (event_type)
 *   - label    : human-readable label
 *   - icon     : emoji glyph rendered in event cards + timeline
 *   - keywords : optional aliases for search / autocomplete
 *
 * Adding a new type = one entry in the appropriate CATEGORY.items array.
 * The picker + timeline pick it up automatically.
 */

export const EVENT_CATEGORIES = [
  {
    id: "celebrations",
    label: "Celebrations",
    icon: "🎉",
    items: [
      { value: "birthday",           label: "Birthday",                 icon: "🎂" },
      { value: "wedding_anniversary",label: "Wedding Anniversary",      icon: "💞" },
      { value: "milestone_birthday", label: "Milestone Birthday",       icon: "🎊" },
      { value: "retirement",         label: "Retirement Celebration",   icon: "🎓" },
      { value: "housewarming",       label: "Housewarming",             icon: "🏡" },
      { value: "family_reunion",     label: "Family Reunion",           icon: "👨‍👩‍👧‍👦" },
      { value: "holiday_gathering",  label: "Holiday Gathering",        icon: "🎄" },
      { value: "family_picnic",      label: "Family Picnic",            icon: "🧺" },
      { value: "farewell",           label: "Farewell Party",           icon: "👋" },
    ],
  },
  {
    id: "birth",
    label: "Birth & Childhood",
    icon: "👶",
    items: [
      { value: "birth",              label: "Birth",                    icon: "👶" },
      { value: "naming_ceremony",    label: "Naming Ceremony",          icon: "📛" },
      { value: "baby_shower",        label: "Baby Shower",              icon: "🍼" },
      { value: "gender_reveal",      label: "Gender Reveal",            icon: "🎈" },
      { value: "first_birthday",     label: "First Birthday",           icon: "🎂" },
      { value: "christening",        label: "Christening / Baptism",    icon: "✝️" },
      { value: "dedication",         label: "Dedication Ceremony",      icon: "🙏" },
      { value: "annaprashan",        label: "Annaprashan (First Feeding)", icon: "🥣" },
      { value: "mundan",             label: "Mundan / Chudakarana",     icon: "✂️" },
      { value: "ear_piercing",       label: "Ear Piercing Ceremony",    icon: "💠" },
      { value: "first_school_day",   label: "First Day of School",      icon: "🎒" },
    ],
  },
  {
    id: "marriage",
    label: "Marriage & Relationships",
    icon: "💍",
    items: [
      { value: "engagement",         label: "Engagement",               icon: "💍" },
      { value: "wedding",            label: "Wedding",                  icon: "💒" },
      { value: "civil_marriage",     label: "Civil Marriage",           icon: "📜" },
      { value: "traditional_wedding",label: "Traditional Wedding",      icon: "🎎" },
      { value: "vow_renewal",        label: "Vow Renewal",              icon: "💐" },
      { value: "silver_anniversary", label: "Silver Anniversary (25)",  icon: "🥈" },
      { value: "golden_anniversary", label: "Golden Anniversary (50)",  icon: "🥇" },
      { value: "diamond_anniversary",label: "Diamond Anniversary (60)", icon: "💎" },
      { value: "divorce",            label: "Divorce",                  icon: "📄" },
    ],
  },
  {
    id: "education",
    label: "Education & Career",
    icon: "🎓",
    items: [
      { value: "graduation",         label: "Graduation",               icon: "🎓" },
      { value: "convocation",        label: "Convocation",              icon: "📜" },
      { value: "new_job",            label: "New Job",                  icon: "💼" },
      { value: "promotion",          label: "Promotion",                icon: "📈" },
      { value: "retirement_career",  label: "Retirement",               icon: "🎁" },
      { value: "military_service",   label: "Military Service",         icon: "🎖️" },
      { value: "award",              label: "Award / Honor",            icon: "🏆" },
      { value: "certification",      label: "Professional Certification", icon: "🎫" },
      { value: "business_launch",    label: "Business Launch",          icon: "🚀" },
    ],
  },
  {
    id: "family",
    label: "Family Milestones",
    icon: "👨‍👩‍👧",
    items: [
      { value: "adoption",           label: "Adoption",                 icon: "🤱" },
      { value: "foster",             label: "Foster Placement",         icon: "🏠" },
      { value: "grandparents",       label: "Becoming Grandparents",    icon: "👴" },
      { value: "moving_home",        label: "Moving to a New Home",     icon: "📦" },
      { value: "immigration",        label: "Immigration",              icon: "✈️" },
      { value: "citizenship",        label: "Citizenship Ceremony",     icon: "🛂" },
    ],
  },
  {
    id: "faith",
    label: "Faith & Culture",
    icon: "🙏",
    items: [
      // Christian
      { value: "baptism",            label: "Baptism",                  icon: "✝️" },
      { value: "confirmation",       label: "Confirmation",             icon: "✝️" },
      { value: "first_communion",    label: "First Communion",          icon: "🍞" },
      { value: "holy_communion",     label: "Holy Communion",           icon: "🍞" },
      { value: "ordination",         label: "Ordination",               icon: "⛪" },
      { value: "church_wedding",     label: "Church Wedding",           icon: "⛪" },
      { value: "church_anniversary", label: "Church Anniversary",       icon: "⛪" },
      { value: "memorial_service_c", label: "Memorial Service",         icon: "🕯️" },
      // Hindu
      { value: "namakaran",          label: "Namakaran (Naming)",       icon: "🕉️" },
      { value: "vidyarambham",       label: "Vidyarambham",             icon: "📚" },
      { value: "upanayanam",         label: "Upanayanam (Sacred Thread)", icon: "🧵" },
      { value: "griha_pravesh",      label: "Griha Pravesh",            icon: "🏠" },
      { value: "sathabhishekam",     label: "Sathabhishekam (80th)",    icon: "🎊" },
      { value: "shashtiapthapoorthi",label: "Shashtiapthapoorthi (60th)", icon: "🎊" },
      { value: "bhima_ratha",        label: "Bhima Ratha Shanthi (70)", icon: "🎊" },
      { value: "shraddha",           label: "Shraddha (Annual Memorial)", icon: "🕯️" },
      { value: "funeral_rites_h",    label: "Hindu Funeral Rites",      icon: "🕯️" },
      // Muslim
      { value: "aqiqah",             label: "Aqiqah",                   icon: "☪️" },
      { value: "nikah",              label: "Nikah",                    icon: "☪️" },
      { value: "walima",             label: "Walima",                   icon: "☪️" },
      { value: "hajj",               label: "Hajj",                     icon: "🕋" },
      { value: "umrah",              label: "Umrah",                    icon: "🕋" },
      { value: "eid",                label: "Eid Celebration",          icon: "☪️" },
      { value: "quran_completion",   label: "Quran Completion",         icon: "📖" },
      // Jewish
      { value: "brit_milah",         label: "Brit Milah",               icon: "✡️" },
      { value: "baby_naming_j",      label: "Baby Naming (Jewish)",     icon: "✡️" },
      { value: "bar_mitzvah",        label: "Bar Mitzvah",              icon: "✡️" },
      { value: "bat_mitzvah",        label: "Bat Mitzvah",              icon: "✡️" },
      { value: "yahrzeit",           label: "Yahrzeit (Memorial)",      icon: "🕯️" },
      // Buddhist
      { value: "ordination_b",       label: "Buddhist Ordination",      icon: "☸️" },
      { value: "vesak",              label: "Vesak Celebration",        icon: "☸️" },
      { value: "meditation_retreat", label: "Meditation Retreat",       icon: "🧘" },
      { value: "temple_ceremony",    label: "Temple Ceremony",          icon: "🛕" },
      // Sikh
      { value: "naam_karan",         label: "Naam Karan",               icon: "☬" },
      { value: "amrit_sanskar",      label: "Amrit Sanskar",            icon: "☬" },
      { value: "anand_karaj",        label: "Anand Karaj (Wedding)",    icon: "☬" },
      { value: "gurpurab",           label: "Gurpurab Celebration",     icon: "☬" },
      // Indian cultural
      { value: "onam",               label: "Onam",                     icon: "🌺" },
      { value: "vishu",              label: "Vishu",                    icon: "🌼" },
      { value: "diwali",             label: "Diwali",                   icon: "🪔" },
      { value: "holi",               label: "Holi",                     icon: "🎨" },
      { value: "pongal",             label: "Pongal",                   icon: "🍚" },
      { value: "navratri",           label: "Navratri",                 icon: "💃" },
      { value: "durga_puja",         label: "Durga Puja",               icon: "🕉️" },
      { value: "ganesh_chaturthi",   label: "Ganesh Chaturthi",         icon: "🕉️" },
      { value: "raksha_bandhan",     label: "Raksha Bandhan",           icon: "🎗️" },
      { value: "karva_chauth",       label: "Karva Chauth",             icon: "🌙" },
      { value: "ugadi",              label: "Ugadi",                    icon: "🌱" },
      { value: "baisakhi",           label: "Baisakhi",                 icon: "🌾" },
      { value: "christmas",          label: "Christmas",                icon: "🎄" },
    ],
  },
  {
    id: "achievements",
    label: "Achievements",
    icon: "🏆",
    items: [
      { value: "academic_award",     label: "Academic Award",           icon: "🏅" },
      { value: "sports_achievement", label: "Sports Achievement",       icon: "🏆" },
      { value: "community_award",    label: "Community Award",          icon: "🎖️" },
      { value: "business_milestone", label: "Business Milestone",       icon: "📊" },
      { value: "publication",        label: "Publication",              icon: "📚" },
      { value: "patent",             label: "Patent",                   icon: "💡" },
      { value: "artistic",           label: "Artistic Achievement",     icon: "🎨" },
      { value: "marathon",           label: "Marathon",                 icon: "🏃" },
      { value: "volunteer",          label: "Volunteer Recognition",    icon: "🤝" },
    ],
  },
  {
    id: "travel",
    label: "Travel & Experiences",
    icon: "🌍",
    items: [
      { value: "family_vacation",    label: "Family Vacation",          icon: "🏖️" },
      { value: "pilgrimage",         label: "Pilgrimage",               icon: "🛕" },
      { value: "cruise",             label: "Cruise",                   icon: "🚢" },
      { value: "heritage_tour",      label: "Heritage Tour",            icon: "🏛️" },
      { value: "trekking",           label: "Trekking Expedition",      icon: "🥾" },
      { value: "world_tour",         label: "World Tour",               icon: "🌏" },
      { value: "recovery",           label: "Recovery Celebration",     icon: "💪" },
      { value: "organ_donation",     label: "Organ Donation",           icon: "🤲" },
      { value: "blood_donation",     label: "Blood Donation Milestone", icon: "🩸" },
    ],
  },
  {
    id: "memorials",
    label: "Memorials",
    icon: "🌹",
    items: [
      { value: "death",              label: "Death",                    icon: "🕯️" },
      { value: "funeral",            label: "Funeral",                  icon: "⚰️" },
      { value: "memorial_service",   label: "Memorial Service",         icon: "🕯️" },
      { value: "annual_remembrance", label: "Annual Remembrance",       icon: "🕯️" },
      { value: "graveside_visit",    label: "Graveside Visit",          icon: "🌹" },
      { value: "celebration_of_life",label: "Celebration of Life",      icon: "🕊️" },
      { value: "obituary",           label: "Obituary Publication",     icon: "📰" },
    ],
  },
  {
    id: "recurring",
    label: "Community & Reunions",
    icon: "📅",
    items: [
      { value: "monthly_dinner",     label: "Monthly Family Dinner",    icon: "🍽️" },
      { value: "cousins_meetup",     label: "Cousins Meetup",           icon: "👥" },
      { value: "annual_reunion",     label: "Annual Reunion",           icon: "📆" },
      { value: "picnic_recurring",   label: "Family Picnic",            icon: "🧺" },
      { value: "holiday_recurring",  label: "Holiday Celebration",      icon: "🎉" },
      { value: "zoom_gathering",     label: "Virtual Family Gathering", icon: "💻" },
    ],
  },
  // Fallback catch-all so old records don't break.
  {
    id: "other",
    label: "Other",
    icon: "📌",
    items: [
      { value: "other",              label: "Other",                    icon: "📌" },
    ],
  },
];

// Fast lookup: eventType → { icon, label, category }
const _lookup = new Map();
for (const cat of EVENT_CATEGORIES) {
  for (const it of cat.items) {
    _lookup.set(it.value, { ...it, categoryId: cat.id, categoryLabel: cat.label });
  }
}

// Legacy fallbacks — accept old free-text values that shipped with earlier
// versions of the app and don't have an explicit entry above.
const LEGACY_MAP = {
  "reunion":    "family_reunion",
  "festival":   "holiday_gathering",
  "anniversary": "wedding_anniversary",
};

export function getEventMeta(type) {
  if (!type) return _lookup.get("other");
  const t = String(type).toLowerCase().trim();
  if (_lookup.has(t)) return _lookup.get(t);
  if (LEGACY_MAP[t] && _lookup.has(LEGACY_MAP[t])) return _lookup.get(LEGACY_MAP[t]);
  // Free-text fallback — humanise the raw type key so it's still readable.
  const humanised = t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return { value: t, label: humanised, icon: "📅", categoryId: "other", categoryLabel: "Other" };
}

/** Return only the categories that have at least one event in `events`
 *  (plus preserving the tab order). */
export function categoriesWithEvents(events) {
  const seen = new Set();
  for (const e of events || []) {
    const meta = getEventMeta(e.event_type);
    if (meta) seen.add(meta.categoryId);
  }
  return EVENT_CATEGORIES.filter((c) => seen.has(c.id));
}
